import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { FocusUtils } from "abreact/incubator/ui/utils/FocusUtils";
import { KeyboardUtilsReact } from "abreact/incubator/ui/utils/KeyboardUtilsReact";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbFilterTextInput } from "abreact/input/AbFilterTextInput";
import { AbList } from "abreact/input/AbList";
import { AbHBox } from "abreact/layout/AbHBox";
import { AbHSeparator } from "abreact/layout/AbHSeparator";
import { AbItemUtils } from "abreact/misc/AbItemUtils";
import { AbPopupButton } from "abreact/misc/AbPopupButton";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import { AbEllipsisAnimation } from "abreact/progress/AbEllipsisAnimation";
import { AbTree, AbTreeType, useTreeRootNode } from "abreact/tree/AbTree";
import { AbTreeUtils } from "abreact/tree/AbTreeUtils";
import { AbTreeRootNodeImpl } from "abreact/tree/node/AbTreeRootNodeImpl";
import { AbFilterUtils } from "abreact/utils/AbFilterUtils";
import * as AbHooks from "abreact/utils/AbHooks";
import { useCurrentValue, useStateWithRef, useSynchronizedCallback } from "abreact/utils/AbHooks";
import { AbSelectionPropsUtils } from "abreact/utils/AbSelectionPropsUtils";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { AbSet } from "abweb/collection/AbSet";
import { ArrayUtils } from "abweb/utils/ArrayUtils";
import { Assert } from "abweb/utils/Assert";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { Lambdas } from "abweb/utils/lambda/Lambdas";
import { PromiseUtils } from "abweb/utils/PromiseUtils";
import * as React from "react";

/**
 * The properties for an AbFilteredTree.
 * Includes all of AbTreeProps, and applies these properties to the AbTree subcomponent.
 * filterFunction and/or onFilterChange must be provided to implement custom filtering support.
 */

export const AbFilteredTree = props => {
  const {
    filterFunction,
    externalFilter,
    filterBoxProps,
    treeProps,
    id,
    dataTestId,
    autoFocus,
    tooltip,
    className,
    keyFunction,
    style,
    onFilterResultsChange,
    abRef,
    externalFilterString,
    onCollapse,
    onExpand,
    itemRenderer,
    noResultsNode,
    ...restOfTreeProps
  } = props;
  const [inputFilterString, setInputFilterString] = React.useState(props.initialFilterString ?? "");
  const filterStringRef = AbHooks.useCurrentValue(props.externalFilterString ?? inputFilterString);
  const filterString = filterStringRef.current;
  const treeRootNode = useTreeRootNode(props.roots, props.keyFunction);
  Assert.assert(ClassUtils.isInstanceOf(treeRootNode, AbTreeRootNodeImpl));
  const currentOnFilterResultsChange = useCurrentValue(onFilterResultsChange);
  const calculatedFilterFunction = props.filterFunction ?? AbFilterUtils.FILTER_FUNCTIONS.SUBSTRING;
  const currentTreeRootNode = useCurrentValue(treeRootNode);
  const textInputRef = React.useRef(null);
  const userExpandedNodes = React.useRef(AbSet.from());
  const userCollapsedAutoExpandNode = React.useRef(AbSet.from());
  const userSelectedNodes = React.useRef(AbSet.from());
  const selectionExpandedNodes = React.useRef(undefined);
  const partialRef = React.useRef({});
  const partialComplete = partial => !!partial.treeRoot;
  const listRef = AbHooks.useComposeRef(props, partialRef.current, "listRef", partialComplete);
  const treeRef = AbHooks.useComposeRef(props, partialRef.current, "treeRef", partialComplete);
  const rootElementRef = AbHooks.useComposeRef(props, partialRef.current, "treeRoot", partialComplete);
  const [filterSets, setFilterSets, filterSetsRef] = useStateWithRef(undefined);
  const matchArrayCache = React.useRef(undefined);
  const [neverCached, setNeverCached] = React.useState(true);
  const [cacheTree, treeCaching] = useSynchronizedCallback(() => AbTreeUtils.cacheAll(treeRootNode));
  const defaultedNoResultsNode = /*#__PURE__*/React.createElement("div", {
    className: "ab-italic ab-filter-no-results ab-disabled"
  }, props.noResultsNode ?? AbReactResources.filter_noResults());
  const [doFilter, filterHappening] = useSynchronizedCallback(async stale => {
    if (filterStringRef.current === "") return;
    const currentFilterString = filterStringRef.current;
    if (!props.externalFilter) {
      const resultPaths = AbTreeUtils.getFilterSets(treeRootNode, (value, enrichment) => calculatedFilterFunction(currentFilterString, value, enrichment));
      // Necessary to allow component to re-render
      await PromiseUtils.sleepZero();
      if (stale.stale) return;
      await applyFilterSets(resultPaths);
      return;
    }
    let resultPaths = props.externalFilter(filterStringRef.current);
    if (!ArrayUtils.isArrayLike(resultPaths)) resultPaths = await resultPaths;
    // Necessary to allow component to re-render
    await PromiseUtils.sleepZero();
    if (stale.stale) return;
    await applyFilterSets(AbTreeUtils.filterPathsToSets(treeRootNode, resultPaths));
  });
  React.useEffect(() => {
    rootElementRef(treeRootNode);
  }, [treeRootNode]);
  AbHooks.useOnUnmount(() => rootElementRef(null));
  const userSelectedToExpandNodes = AbHooks.useCurrentCallback(selected => {
    if (selectionExpandedNodes.current) return selectionExpandedNodes.current;
    const returnSet = AbSet.from();
    selected.forEach(node => {
      let currNode = node.parent;
      while (!AbTreeUtils.isTreeRoot(currNode)) {
        // If already added, no need to duplicate work
        if (returnSet.has(currNode)) break;
        returnSet.add(currNode);
        currNode = currNode.parent;
      }
    });
    selectionExpandedNodes.current = returnSet;
    return returnSet;
  });
  const setsToMatchArray = AbHooks.useCurrentCallback(selected => {
    if (!matchArrayCache.current) matchArrayCache.current = [...selected.map(node => node.enrichedPath)];
    return matchArrayCache.current;
  });
  React.useEffect(() => {
    cacheTree();
    setNeverCached(false);
  }, [props.dataFreshness, props.roots]);
  const applyFilterSets = async newFilterSets => {
    if (filterStringRef.current === "") return;
    const nodesToCollapse = filterSetsRef.current?.expandedNodes.difference(newFilterSets.expandedNodes).difference(userExpandedNodes.current).difference(userSelectedToExpandNodes(userSelectedNodes.current)).filter(node => {
      // Should collapse if not default expanded or if the user explicitly collapsed it
      return !node.enrichment?.defaultExpanded || userCollapsedAutoExpandNode.current.has(node);
    });
    const nodesToExpand = filterSetsRef.current ? newFilterSets.expandedNodes.difference(filterSetsRef.current.expandedNodes) : newFilterSets.expandedNodes;
    setFilterSets(newFilterSets);
    matchArrayCache.current = undefined;
    if (!props.flatten) {
      treeRootNode.setFilterSets(newFilterSets);
      await treeRootNode.doBatchAction(() => {
        nodesToCollapse?.forEach(node => node.collapse());
        const expandPromises = [...nodesToExpand.map(node => node.expander?.expand())].filter(Lambdas.isDefined);
        if (expandPromises.every(p => p === undefined)) return undefined;
        return Promise.all(expandPromises).then();
      }, false);
    }
    currentOnFilterResultsChange.current?.(newFilterSets);
  };
  React.useEffect(() => {
    if (!treeCaching) {
      doFilter();
    }
    if (treeCaching || !filterStringRef.current) {
      if (filterSetsRef.current) {
        // cleanup after filter
        const nodesToCollapse = filterSetsRef.current?.expandedNodes.difference(userExpandedNodes.current).difference(userSelectedToExpandNodes(userSelectedNodes.current)).filter(node => {
          // Should collapse if not default expanded or if the user explicitly collapsed it
          return !node.enrichment?.defaultExpanded || userCollapsedAutoExpandNode.current.has(node);
        });
        nodesToCollapse.forEach(node => node.collapse());
        setFilterSets(undefined);
        currentTreeRootNode.current.setFilterSets(undefined);
        matchArrayCache.current = undefined;
        currentOnFilterResultsChange.current?.(undefined);
      }
    }
  }, [filterString, treeCaching]);
  const itemRendererToPass = React.useMemo(() => {
    if (!itemRenderer) return undefined;
    return (item, enrichment, treeProps) => {
      return itemRenderer?.(item, enrichment, {
        filterString: filterStringRef.current,
        path: treeProps.path,
        enrichedPath: treeProps.enrichedPath,
        isMatch: filterStringRef.current.length > 0 && (calculatedFilterFunction?.(filterStringRef.current, item, enrichment) ?? false)
      });
    };
  }, [itemRenderer, calculatedFilterFunction]);
  const filterBoxKeyHandler = KeyboardUtilsReact.makeKeyHandler([[KeyValue.ARROW_DOWN, e => {
    e.preventDefault();
    (partialRef.current.listRef ?? partialRef.current.treeRef)?.focusFirstFocusable();
  }], [KeyValue.ENTER, () => {
    // Enter selects the first selectable item, if one exists (or if multiselect and the item is already selected, de-selects)
    // only enabled if there's something typed in the filter box
    if (filterStringRef.current.length > 0 && filterSets?.matchedNodes.size === 1) {
      const path = [...filterSets.matchedNodes][0].enrichedPath;
      const enrichment = AbEnriched.getEnrichment(path[path.length - 1]);
      if (!AbEnriched.isSelectable(enrichment)) return;
      if (props.type === AbTreeType.UNIQUE) {
        AbSelectionPropsUtils.selectionPropsSelect({
          ...props
        }, AbEnriched.getItem(path[path.length - 1]), path[path.length - 1]);
      } else {
        AbSelectionPropsUtils.selectionPropsSelect({
          ...props,
          keyFunction: AbTreeUtils.makePathKeyFunction(props.keyFunction)
        }, AbEnriched.getItems(path), AbEnriched.createMaybe(AbEnriched.getItems(path), enrichment));
      }
    }
  }]]);

  // wrap key handler for tabbing because key handler prevents default
  const filterBoxOnKeyDown = e => {
    props.filterBoxProps?.__htmlProps?.onKeyDown?.(e);
    filterBoxKeyHandler(e);
  };
  const onSelect = enrichedPath => {
    const path = AbEnriched.getItems(enrichedPath);
    props.onSelect?.(path);
    const node = AbTreeUtils.findCached(treeRootNode, path);
    if (!node) return;
    treeRootNode.doBatchAction(() => {
      return AbTreeUtils.expandPath(treeRootNode, path.slice(0, -1));
    }, false);
    selectionExpandedNodes.current = undefined;
    userSelectedNodes.current.add(node);
  };
  const onUnselect = enrichedPath => {
    const path = AbEnriched.getItems(enrichedPath);
    props.onUnselect?.(path);
    const node = AbTreeUtils.findCached(treeRootNode, path);
    if (!node) return;
    selectionExpandedNodes.current = undefined;
    userSelectedNodes.current.delete(node);
  };
  const renderTree = () => {
    return /*#__PURE__*/React.createElement(AbTree, _extends({}, restOfTreeProps, treeProps, {
      onExpand: path => {
        if (treeRootNode.batchActionBusy) return;
        onExpand?.(path);
        const node = AbTreeUtils.find(treeRootNode, path);
        if (!node) return;
        userExpandedNodes.current.add(node);
      },
      onCollapse: path => {
        if (treeRootNode.batchActionBusy) return;
        onCollapse?.(path);
        const node = AbTreeUtils.find(treeRootNode, path);
        if (!node) return;
        if (node.enrichment?.defaultExpanded) userCollapsedAutoExpandNode.current.add(node);
        userExpandedNodes.current.delete(node);
      },
      onSelect: onSelect,
      onUnselect: onUnselect,
      itemEventHandlers: {
        ...restOfTreeProps.itemEventHandlers,
        onKeyDown: (item, event) => {
          restOfTreeProps.itemEventHandlers?.onKeyDown?.(item, event);
          if (event.key === KeyValue.ARROW_UP && partialRef.current.treeRef?.isFirstFocused?.()) {
            event.stopPropagation();
            event.preventDefault();
            FocusUtils.setFocus(textInputRef.current?.inputElement);
          }
        }
      },
      abRef: treeRef,
      itemRenderer: itemRendererToPass,
      roots: treeRootNode,
      keyFunction: props.keyFunction
    }));
  };

  // States:
  // Haven't finished caching the tree
  // Only search visible and no results
  // No filter string
  // filter string and no results
  // filter string and flat
  // filter string and hierarchical
  const renderContent = () => {
    // Return null indicates "just display the tree"
    if (treeCaching || filterHappening || neverCached) return /*#__PURE__*/React.createElement(AbEllipsisAnimation, null);
    if (treeRootNode.size === 0) return defaultedNoResultsNode;
    if (!filterStringRef.current || !filterSets) return renderTree();
    if (filterSets.matchedNodes.size === 0) return defaultedNoResultsNode;
    if (props.flatten) {
      let selectionProps;
      if (props.type === AbTreeType.UNIQUE) {
        selectionProps = AbSelectionPropsUtils.mapSelectionProps({
          ...props
        }, item => [item], path => AbEnriched.getItem(path[path.length - 1]));
      } else {
        selectionProps = AbSelectionPropsUtils.mapSelectionProps({
          ...props,
          keyFunction: AbTreeUtils.makePathKeyFunction(props.keyFunction)
        }, path => path, AbEnriched.getItems);
      }
      return /*#__PURE__*/React.createElement(AbList, _extends({
        items: setsToMatchArray(filterSets.matchedNodes),
        className: "ab-filtered-tree-flat-results",
        abRef: listRef
      }, selectionProps, {
        virtualized: props.virtualized,
        onSelect: onSelect,
        onUnselect: onUnselect,
        itemEventHandlers: {
          onKeyDown: (_, event) => {
            if (event.key === KeyValue.ARROW_UP && partialRef.current.listRef?.isFirstFocused()) {
              event.stopPropagation();
              event.preventDefault();
              FocusUtils.setFocus(textInputRef.current?.inputElement);
            }
          }
        },
        itemRenderer: path => {
          const lastItem = path[path.length - 1];
          if (props.itemRenderer) return props.itemRenderer(AbEnriched.getItem(lastItem), AbEnriched.getEnrichment(lastItem), {
            filterString: filterStringRef.current,
            path: AbEnriched.getItems(path),
            enrichedPath: path,
            isMatch: true
          });
          return AbItemUtils.defaultItemDisplayName(lastItem);
        }
      }));
    }
    return renderTree();
  };
  const renderFilterBox = () => {
    const {
      onChange: filterBoxOnChange,
      textInputDebounce,
      __htmlProps: __filterBoxHtmlProps,
      abRef: filterBoxPropsAbRef,
      placeholder: filterBoxPlaceholder,
      ...rest
    } = filterBoxProps ?? {};
    return /*#__PURE__*/React.createElement(AbHBox, {
      className: ReactUtils.classNamesString(["ab-filtered-picker-text-input-container", props.advancedFilterButtonProps ? "with-settings-button" : undefined])
    }, /*#__PURE__*/React.createElement(AbFilterTextInput, {
      className: ReactUtils.classNamesString(["ab-filtered-picker-input"]),
      value: filterString,
      onChange: text => {
        filterBoxOnChange?.(text);
        setInputFilterString(text);
      },
      textInputDebounce: textInputDebounce ?? "default",
      textInputProps: {
        ...rest,
        abRef: ReactUtils.mergeRefs(filterBoxPropsAbRef, textInputRef),
        __htmlProps: {
          ...__filterBoxHtmlProps,
          autoComplete: "off",
          onKeyDown: filterBoxOnKeyDown
        },
        disabled: props.disabled,
        tabIndex: props.tabIndex,
        placeholder: filterBoxPlaceholder ?? AbReactResources.filterTextInput_emptyText(),
        autoFocus: autoFocus
      }
    }), props.advancedFilterButtonProps && /*#__PURE__*/React.createElement(AbPopupButton, _extends({}, props.advancedFilterButtonProps, {
      icon: "absdk:settings",
      disabled: props.disabled,
      className: "ab-filtered-tree-settings-button ab-no-border-button"
    })));
  };
  return /*#__PURE__*/React.createElement(AbWithTooltip, {
    tooltip: tooltip
  }, /*#__PURE__*/React.createElement("div", ReactUtils.componentProps("ab-filtered-tree", props, ["ab-filtered-picker", props.disabled && "ab-disabled"]), props.externalFilterString === undefined && /*#__PURE__*/React.createElement(React.Fragment, null, renderFilterBox(), /*#__PURE__*/React.createElement(AbHSeparator, null)), /*#__PURE__*/React.createElement("div", {
    className: "ab-filtered-picker-content"
  }, renderContent())));
};
//# sourceMappingURL=AbFilteredTree.js.map
